import * as PIXI from 'pixi.js';
import {
  buildLadderStrokeCommands,
  buildWinnerPolyline,
  ladderBoardLayoutInRect,
  type MinionsResult,
  type MinionsStart,
  ladderGeom,
} from '../shared/minionsLogic';

const RAIL = 0x64748b;
const RUNG = 0x94a3b8;
const WIN = 0xfacc15;

/** 승리 폴리라인을 길이 비율 t(0~1)만큼만 잘라 반환 (연속 좌표 x,y,…) */
function clipPolylineByProgress(pts: number[], t: number): number[] {
  if (pts.length < 4 || t <= 0) return [];
  if (t >= 1) return pts;
  let total = 0;
  const segLen: number[] = [];
  for (let i = 0; i < pts.length - 2; i += 2) {
    const dx = pts[i + 2] - pts[i];
    const dy = pts[i + 3] - pts[i + 1];
    segLen.push(Math.hypot(dx, dy));
    total += segLen[segLen.length - 1];
  }
  if (total <= 0) return pts;
  let remain = total * t;
  const out: number[] = [pts[0], pts[1]];
  for (let s = 0; s < segLen.length; s++) {
    const L = segLen[s];
    const i = s * 2;
    if (remain <= L) {
      const r = L > 0 ? remain / L : 1;
      out.push(pts[i] + r * (pts[i + 2] - pts[i]), pts[i + 1] + r * (pts[i + 3] - pts[i + 1]));
      break;
    }
    remain -= L;
    out.push(pts[i + 2], pts[i + 3]);
  }
  return out;
}

export class LadderBoard extends PIXI.Container {
  private railsG: PIXI.Graphics;
  private winG: PIXI.Graphics;
  private designW = 200;
  private designH = 280;
  /** 빠른 연속 consume 시 이전 Ticker 루프 무효화 */
  private winnerPathAnimToken = 0;

  constructor() {
    super();
    this.railsG = new PIXI.Graphics();
    this.winG = new PIXI.Graphics();
    this.addChild(this.railsG);
    this.addChild(this.winG);
  }

  /** 전체 캔버스 기준(레거시) */
  layout(width: number, height: number) {
    const pad = 24;
    const availW = Math.max(100, width - pad * 2);
    const availH = Math.max(100, height - pad * 2);
    const s = Math.min(availW / this.designW, availH / this.designH);
    this.scale.set(s);
    this.position.set((width - this.designW * s) / 2, pad + (availH - this.designH * s) / 2);
  }

  /** 중앙 패널 사각형 안에 맞춤 (baccara 스타일 레이아웃) */
  layoutInRect(w: number, h: number) {
    const { s, bx, by } = ladderBoardLayoutInRect(w, h);
    this.scale.set(s);
    this.position.set(bx, by);
  }

  clearWinner() {
    this.winnerPathAnimToken++;
    this.winG.clear();
  }

  drawStatic(start: MinionsStart, lines: 3 | 4, result: MinionsResult, hash?: string) {
    const { rails, bridges } = buildLadderStrokeCommands(start, lines, result, hash);
    const g = this.railsG;
    g.clear();
    g.lineStyle(3, RAIL, 1);
    for (const [x0, y0, x1, y1] of rails) {
      g.moveTo(x0, y0);
      g.lineTo(x1, y1);
    }
    g.lineStyle(2, RUNG, 0.95);
    const { LADDER_LEFT_X, LADDER_RIGHT_X } = ladderGeom;
    for (const b of bridges) {
      const midX = (LADDER_LEFT_X + LADDER_RIGHT_X) / 2;
      const ctrlY = b.y + 3;
      g.moveTo(LADDER_LEFT_X, b.y);
      g.quadraticCurveTo(midX, ctrlY, LADDER_RIGHT_X, b.y);
    }
  }

  /**
   * 승리 경로를 위→아래로 따라 그리듯 표시 (ethminions 당첨 경로 연출에 대응).
   * 기존에는 한 프레임에 전체 선만 그렸음.
   */
  drawWinnerPath(
    start: MinionsStart,
    lines: 3 | 4,
    result: MinionsResult,
    hash?: string,
    durationMs = 880,
    easing: 'linear' | 'easeOutCubic' = 'easeOutCubic'
  ) {
    const pts = buildWinnerPolyline(start, lines, result, hash);
    if (pts.length < 4) {
      this.winnerPathAnimToken++;
      this.winG.clear();
      return;
    }
    const token = ++this.winnerPathAnimToken;
    const g = this.winG;
    g.clear();
    const dur = Math.max(200, Math.min(30000, durationMs));
    const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const ticker = PIXI.Ticker.shared;
    const tick = () => {
      if (token !== this.winnerPathAnimToken) {
        ticker.remove(tick);
        return;
      }
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const u = Math.min(1, (now - t0) / dur);
      const t =
        easing === 'linear'
          ? u
          : /* easeOutCubic — 끝에서 살짝 감속 */
            1 - (1 - u) ** 3;
      const clipped = clipPolylineByProgress(pts, t);
      g.clear();
      if (clipped.length >= 4) {
        g.lineStyle(4, WIN, 1);
        g.moveTo(clipped[0], clipped[1]);
        for (let i = 2; i < clipped.length; i += 2) {
          g.lineTo(clipped[i], clipped[i + 1]);
        }
      }
      if (u >= 1 || token !== this.winnerPathAnimToken) {
        ticker.remove(tick);
      }
    };
    ticker.add(tick);
  }

  flashBackground() {
    const flash = new PIXI.Graphics();
    flash.beginFill(0xffffff, 0.12);
    flash.drawRect(0, 0, this.designW, this.designH);
    flash.endFill();
    this.addChild(flash);
    setTimeout(() => {
      this.removeChild(flash);
      flash.destroy();
    }, 120);
  }
}

/**
 * PIXI `resolution` = 캔버스 비트맵 배율. `1`이면 `canvas.width` ≈ CSS px(DPR 무시).
 * 레티나에서 선·텍스트는 다소 번질 수 있음. 고해상도 유지는 `.env`에
 * `VITE_MINIONS_RENDERER_RESOLUTION=device` (빌드 후 재시작).
 */
export function minionsRendererResolution(): number {
  const mode =
    typeof import.meta !== 'undefined' && import.meta.env?.VITE_MINIONS_RENDERER_RESOLUTION;
  if (mode === 'device' && typeof window !== 'undefined') {
    const dpr = window.devicePixelRatio || 1;
    return Math.min(4, Math.max(1, Math.ceil(dpr)));
  }
  return 1;
}

function visibleViewportHeightPx(): number | null {
  if (typeof window === 'undefined') return null;
  const h = Math.round(window.visualViewport?.height ?? window.innerHeight);
  return h > 48 ? h : null;
}

function visibleViewportWidthPx(): number | null {
  if (typeof window === 'undefined') return null;
  const w = Math.round(window.visualViewport?.width ?? window.innerWidth);
  return w > 48 ? w : null;
}

/**
 * getBoundingClientRect·clientWidth·inner* 가 서로 ~2배로 어긋나는 경우(모바일 WebView·임베드):
 * 논리 캔버스가 절반만 잡히면 표시는 늘어나 "200% 확대"처럼 보인다.
 * 이때는 visualViewport / inner 를 우선한다.
 */
function reconcileCssSize(
  rect: number,
  client: number,
  vv: number,
  inner: number,
  clamp: (raw: number) => number
): number {
  const r = Math.round(rect);
  const c = Math.round(client);
  const v = Math.round(vv);
  const i = Math.round(inner);
  const pool = [r, c, v, i].filter((x) => x > 16);
  if (pool.length === 0) return Math.max(1, clamp(r));
  const lo = Math.min(...pool);
  const hi = Math.max(...pool);
  let out = r;
  if (hi > lo * 1.42) {
    out = Math.min(v > 16 ? v : i, i > 16 ? i : v, c > 16 ? c : r);
    if (c > 16 && c < hi * 0.92) out = Math.min(out, c);
  }
  return Math.max(1, Math.round(clamp(out)));
}

/**
 * 임베드(헤더·flex 하단) 시 부모(#app)만 쓰되,
 * 부모가 100dvh·flex 등으로 가시 뷰포트보다 과도하게 길면 버퍼만 길어지고
 * 중앙 UI(ethDecor 세로 중앙 정렬)가 화면 아래로 밀린다 → visualViewport 높이로 상한.
 * 가로도 동일하게 넓으면 캔버스 왼쪽만 보이고 게임이 오른쪽으로 밀려 보인다.
 */
export function readCanvasContainerSize(canvas: HTMLCanvasElement): { w: number; h: number } {
  const capH = visibleViewportHeightPx();
  const capW = visibleViewportWidthPx();
  const innerW = typeof window !== 'undefined' ? window.innerWidth : 800;
  const innerH = typeof window !== 'undefined' ? window.innerHeight : 600;
  const vvW = typeof window !== 'undefined' ? (window.visualViewport?.width ?? innerW) : innerW;
  const vvH = typeof window !== 'undefined' ? (window.visualViewport?.height ?? innerH) : innerH;
  const clampH = (raw: number) =>
    capH != null && raw > capH + 16 ? capH : raw;
  const clampW = (raw: number) =>
    capW != null && raw > capW + 16 ? capW : raw;

  const p = canvas.parentElement;
  if (p) {
    const br = p.getBoundingClientRect();
    let rw = br.width;
    let rh = br.height;
    if (rw < 2) rw = p.clientWidth;
    if (rh < 2) rh = p.clientHeight;
    if (rw < 1 && typeof window !== 'undefined') rw = innerW;
    if (rh < 1 && typeof window !== 'undefined') rh = innerH;
    const cw = p.clientWidth || rw;
    const ch = p.clientHeight || rh;
    return {
      w: reconcileCssSize(rw, cw, vvW, innerW, clampW),
      h: reconcileCssSize(rh, ch, vvH, innerH, clampH),
    };
  }
  return {
    w: Math.max(1, Math.round(clampW(innerW))),
    h: Math.max(1, Math.round(clampH(innerH))),
  };
}

export function createMinionsApp(canvas: HTMLCanvasElement) {
  const { w, h } = readCanvasContainerSize(canvas);
  /** file:// + WebGL 은 로컬/캔버스 텍스처에서 texImage2D SecurityError 가 자주 남 — Canvas 렌더러로 회피 */
  const fileOpen =
    typeof globalThis.location?.protocol === 'string' &&
    globalThis.location.protocol === 'file:';
  return new PIXI.Application({
    view: canvas,
    width: w,
    height: h,
    backgroundAlpha: 0,
    antialias: true,
    resolution: minionsRendererResolution(),
    autoDensity: true,
    forceCanvas: fileOpen,
  });
}
